import * as React from 'react';
import { memo, useCallback } from 'react';
import { useField, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { AdditionalTravellerFieldEnum, InputType } from '../../types';
import { PassengerSwitch } from './PassengerSwitch/PassengerSwitch';
import Input from '../../../Input';
import { Select } from './Select/Select';
import { DateField } from './Date/Date';
import PhoneInput from '../../../PhoneInput';
import DocumentType from './DocumentType/DocumentType';
import LastName from './LastName/LastName';
import { fieldIsRequired, formatInvalidPassengerDate, validate, validateDocNumberLength } from '../../utils';
import DocExpiryDate from './DocExpiryDate/DocExpiryDate';
import { useMediaQuery } from 'react-responsive';
import { disableAutocompleteProps, MOBILE_MIN_WIDTH } from '../../../utils';
import { DocTypes, TravellerFieldEnum } from '@websky/graphql';
import { LinkedTraveller } from './LinkedTraveller/LinkedTraveller';
import Nationality from './Nationality/Nationality';
import { isNameFieldType } from '../../../PassengerForm/utils';
import PassengerSubsidySelect from './PassengerSubsidySelect';
export const Field = memo(props => {
    var _a;
    const { t } = useTranslation('Passenger');
    const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
    const isDocNumber = props.field.type === TravellerFieldEnum.DocNumber;
    const isBirthRegDocument = props.passengerDocType === DocTypes.BirthRegDocument;
    const escapedT = (label) => typeof label !== 'undefined'
        ? t(label, {
            // This is necessary to avoid colon problems in error messages.
            nsSeparator: '|',
            keySeparator: '>'
        })
        : label;
    const fieldState = useField(props.field.name, {
        initialValue: props.field.defaultValue,
        type: props.field.input === InputType.Switch ? undefined : props.field.input,
        validate: props.disabled
            ? () => { }
            : (value, allValues) => {
                var _a, _b;
                const validatedData = validate(value, (_b = (_a = allValues['passengers']) === null || _a === void 0 ? void 0 : _a[props.passengerId]) !== null && _b !== void 0 ? _b : {}, props.field.input, props.field.validations, props.field.type, props.field.defaultValue, allValues['passengers']);
                if (props.field.type === TravellerFieldEnum.LinkedTraveller) {
                    return validatedData;
                }
                return escapedT(validatedData);
            },
        parse: isNameFieldType(props.field.type) ? value => value !== null && value !== void 0 ? value : '' : undefined
    });
    const onChangeSelectHandler = useCallback((value, event) => {
        props.onChange && props.onChange(props.field.name, value);
        fieldState.input.onChange(event);
    }, [props.onChange, fieldState]);
    const error = fieldState.meta.error &&
        (fieldState.meta.submitFailed ||
            (fieldState.meta.touched && fieldState.meta.modified) ||
            (fieldState.input.value && !fieldState.meta.modified))
        ? fieldState.meta.error
        : fieldState.meta.submitError && !fieldState.meta.dirtySinceLastSubmit
            ? fieldState.meta.submitError
            : '';
    const isRequired = props.field.validations && ((_a = props.field.validations[0]) === null || _a === void 0 ? void 0 : _a.required);
    const label = isRequired ? `${props.field.placeholder}*` : props.field.placeholder;
    const inputVariant = props.variant ? props.variant : isMobile ? 'standard' : 'filled';
    let inputMode = 'text';
    // Numeric input mode
    if (['LoyaltyNumber', 'LoyaltyAirline'].includes(props.field.type)) {
        inputMode = 'numeric';
    }
    const { mutators } = useForm();
    const { dirty } = useFormState();
    const onProfileDocumentSelectHandler = useCallback((fillValue) => {
        // поле даты действия документа скрыто, не нужно заполнять его в мутаторе
        // так как может быть невалидным а поскольку скрыто, пользователь не сможет исправить
        mutators.fillFromDocument(props.passengerId, fillValue, props.docExpiryDateField);
    }, [props.passengerId, props.docExpiryDateField]);
    const clearFieldsHandler = () => {
        const mutator = mutators['clearFields'];
        mutator(props.passengerId);
    };
    if (props.field.input === InputType.Text) {
        if (props.field.type === TravellerFieldEnum.LinkedTraveller) {
            return (React.createElement(LinkedTraveller, { value: fieldState.input.value, validations: props.field.validations, name: props.field.name, label: label, className: props.className, onChange: onChangeSelectHandler, onBlur: fieldState.input.onBlur, error: t(fieldState.meta.error), disabled: props.disabled, variant: inputVariant }));
        }
        const { values } = useFormState();
        return (React.createElement(Input, Object.assign({ label: label, value: fieldState.input.value, name: props.field.name, className: props.className, disabled: props.disabled, autoFocus: props.autofocus, variant: inputVariant, TextFieldProps: {
                onChange: (e) => {
                    if (isDocNumber) {
                        const isValid = validateDocNumberLength(e.target.value, values['passengers'][props.passengerId], props.field.validations);
                        if (!isValid) {
                            return;
                        }
                        if (isBirthRegDocument) {
                            e.target.value = e.target.value.toUpperCase();
                        }
                    }
                    props.onChange && props.onChange(props.field.name, e.target.value);
                    fieldState.input.onChange(e);
                },
                onBlur: (e) => {
                    fieldState.input.onBlur(e);
                },
                error: !!error,
                helperText: error
            }, absoluteHintPosition: true, inputMode: inputMode }, disableAutocompleteProps)));
    }
    if (props.field.input === InputType.Phone) {
        return (React.createElement(PhoneInput, { className: props.className, value: fieldState.input.value, disabled: props.disabled, textFieldsProps: {
                label,
                variant: inputVariant,
                name: props.field.name,
                onChange: ((value) => {
                    props.onChange && props.onChange(props.field.name, value);
                    // Fucking `react-phone-number-input` changes the `onChange` handler signature
                    // and passes in string value instead of ChangeEvent,
                    // so we have to create our own event object.
                    fieldState.input.onChange(Object.assign(Object.assign({}, new Event('change')), { target: Object.assign(Object.assign({}, fieldState.input), { value: value }) }));
                }),
                onBlur: (e) => {
                    fieldState.input.onBlur(e);
                },
                error: !!error,
                helperText: error
            } }));
    }
    if (props.field.type === TravellerFieldEnum.DocExpiryDate) {
        const value = formatInvalidPassengerDate(props.field.type, fieldState.input.value);
        const checkFieldIsRequired = useCallback((allValues) => {
            return fieldIsRequired(allValues['passengers'][props.passengerId], props.field.validations);
        }, [props.field.validations]);
        return (React.createElement(DocExpiryDate, { checkFieldIsRequired: checkFieldIsRequired, label: props.field.placeholder, value: value, name: props.field.name, className: props.className, onChange: (e) => {
                props.onChange && props.onChange(props.field.name, e.target.value);
                fieldState.input.onChange(e);
            }, onBlur: (e) => {
                fieldState.input.onBlur(e);
            }, error: error, absoluteHintPosition: true, disabled: props.disabled, variant: inputVariant }));
    }
    if (props.field.input === InputType.Date) {
        const value = formatInvalidPassengerDate(props.field.type, fieldState.input.value);
        return (React.createElement(DateField, { label: label, value: value, name: props.field.name, className: props.className, onChange: (e) => {
                props.onChange && props.onChange(props.field.name, e.target.value);
                fieldState.input.onChange(e);
            }, onBlur: (e) => {
                fieldState.input.onBlur(e);
            }, error: error, absoluteHintPosition: true, disabled: props.disabled, variant: inputVariant }));
    }
    if (props.field.input === InputType.Switch) {
        return (React.createElement(PassengerSwitch, { value: fieldState.input.value ? fieldState.input.value[0] : fieldState.meta.initial, name: props.field.name, options: props.field.validations[0].options, className: props.className, onChange: (value, event) => {
                props.onChange && props.onChange(props.field.name, value);
                fieldState.input.onChange(event);
            }, onBlur: () => fieldState.input.onBlur(), error: error, disabled: props.disabled }));
    }
    if (props.field.input === InputType.Autocomplete) {
        return (React.createElement(LastName, Object.assign({}, props, { onClear: dirty ? clearFieldsHandler : null, label: label, error: error, field: props.field, onFillFromDocument: onProfileDocumentSelectHandler, onChange: (value, event) => {
                props.onChange && props.onChange(props.field.name, value);
                fieldState.input.onChange(event);
            }, onBlur: () => fieldState.input.onBlur(), passengerId: props.passengerId, passengerType: props.passengerType, fieldState: fieldState, loadTravellers: props.loadTravellers, disabled: props.disabled, variant: inputVariant, onSwap: props.onSwap })));
    }
    if (props.field.input === InputType.Nationality) {
        return (React.createElement(Nationality, Object.assign({}, props, { field: props.field, label: label, error: error, fieldState: fieldState, options: props.field.validations[0].options, className: props.className, onChange: (value, event) => {
                props.onChange && props.onChange(props.field.name, value);
                fieldState.input.onChange(event);
            }, onBlur: () => fieldState.input.onBlur(), disabled: props.disabled, variant: inputVariant })));
    }
    if (props.field.input === InputType.Select) {
        if (props.field.type === TravellerFieldEnum.DocType) {
            return (React.createElement(DocumentType, Object.assign({ label: label, error: error, fieldState: fieldState, onChange: onChangeSelectHandler, variant: inputVariant }, props)));
        }
        const isSubsidyField = props.field.type === AdditionalTravellerFieldEnum.Subsidy;
        const subsidyField = isSubsidyField ? props.field : null;
        if (isSubsidyField && (subsidyField === null || subsidyField === void 0 ? void 0 : subsidyField.options)) {
            return (React.createElement(PassengerSubsidySelect, { name: props.field.name, options: subsidyField.options, value: fieldState.input.value, onChange: (value, event) => {
                    var _a;
                    (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, props.field.name, value);
                    fieldState.input.onChange(event);
                }, onBlur: () => fieldState.input.onBlur(), disabled: props.disabled, error: error, className: props.className, multiple: false }));
        }
        const selectOptions = isSubsidyField && (subsidyField === null || subsidyField === void 0 ? void 0 : subsidyField.options)
            ? subsidyField.options.map(option => ({
                key: option.key,
                value: option.label,
                disabled: option.disabled
            }))
            : props.field.validations[0].options.map(option => ({
                key: option.value,
                value: option.label
            }));
        return (React.createElement(Select, { value: fieldState.input.value, options: selectOptions, variant: inputVariant, name: props.field.name, label: label, className: props.className, onChange: onChangeSelectHandler, onBlur: fieldState.input.onBlur, error: error, disabled: props.disabled, translateValue: props.translateSelectOption, menuProps: props.menuProps, disableAutoSelect: isSubsidyField }));
    }
    return React.createElement(React.Fragment, null);
});
